import type { CustomerDebtCollection } from '@models/customers';
import { UserPermissions } from '@models/user';
import { useCancelCustomerDebtsMutation } from '@features/customers/mutations';
import { useMeStore } from '@features/users/store';
import { useConfirm } from '@hooks/useConfirm';
import { userHasPermission } from '@utils/access';

import s from './CancelDebtsButton.module.css';

type CancelDebtsButtonProps = {
  customerId: string;
  debtCollections: CustomerDebtCollection[];
};

function CancelDebtsButton({ customerId, debtCollections }: CancelDebtsButtonProps) {
  const cancelDebts = useCancelCustomerDebtsMutation();
  const confirm = useConfirm();
  const me = useMeStore();

  // Canceling debts is a write action
  if (!me || !userHasPermission(me, UserPermissions.EDIT_USERS)) return null;

  const hasActiveDebts = debtCollections.some((debt) =>
    ['pending', 'processing'].includes(debt.status.toLowerCase()),
  );

  const handleClick = async () => {
    const confirmed = await confirm(
      'This will mark active debts as failed and cancel upcoming charge attempts.',
      {
        title: 'Cancel customer debts?',
        confirmLabel: 'Cancel debts',
        cancelLabel: 'Keep',
      },
    );
    if (!confirmed) return;
    cancelDebts.mutate(customerId);
  };

  return (
    <button
      type="button"
      className={s.dangerBtn}
      onClick={handleClick}
      disabled={!hasActiveDebts || cancelDebts.isPending}>
      {cancelDebts.isPending ? 'Canceling…' : 'Cancel customer debts'}
    </button>
  );
}

export default CancelDebtsButton;
